export type ContractStatus = 'draft' | 'active' | 'expired' | 'terminated';

export type ContractStatusFilter = 'all' | ContractStatus;

export interface ContractTileCounts {
  all: number;
  draft: number;
  active: number;
  expired: number;
  terminated: number;
}

export interface ContractListRow {
  id: string;
  number: string;
  type?: string;
  title: string;
  status: string;
  operatingRegion: string;
  partyNames?: string;
  wellApis?: string;
}

export interface ContractDetail {
  id: string;
  number: string;
  type: string;
  title: string;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string;
  operatingRegion: string;
  notes?: string;
  citationIsOperator: boolean;
  royalty?: number;
  paidUp: boolean;
  primaryTermMonths?: number;
  recordingNumber?: string;
  landFileId?: string;
  parties: { partyId: string; role: string; legalName?: string }[];
  wells: { wellId: string; apiNumber?: string; name?: string }[];
  tracts: { tractId: string; name?: string }[];
  activity: string[];
  joa?: {
    operatorPartyId: string;
    operatorName?: string;
    operatedByUs: boolean;
    copasVintage: string;
    copasNote?: string;
    relatedContractId?: string;
    relatedContractNumber?: string;
    drillingOverhead?: number;
    producingOverhead?: number;
    overheadUnit?: string;
    partiesDisplay?: string;
    exhibitA: {
      wellId: string;
      included: boolean;
      note?: string;
      apiNumber?: string;
      name?: string;
    }[];
  };
  assignment?: {
    relatedContractId: string;
    relatedContractNumber?: string;
    recordedDate?: string;
    instrumentNumber?: string;
    county?: string;
    state?: string;
    lines: {
      assignorOwnerId: string;
      assigneeOwnerId: string;
      interestClass: string;
      assignedWi: string;
      assignedNri: string;
      assignorName?: string;
      assigneeName?: string;
    }[];
  };
}

export const CONTRACT_STATUSES: ContractStatus[] = ['draft', 'active', 'expired', 'terminated'];

export function normalizeContractStatus(status: string): string {
  return status.trim().toLowerCase();
}

export function formatContractType(type: string): string {
  const raw = type.trim();
  if (raw === 'AssignmentOfOilAndGasInterests') {
    return 'Assignment of Oil and Gas Interests';
  }
  if (raw === 'OilGasLease') {
    return 'Oil and Gas Lease';
  }
  if (raw === 'JointOperatingAgreement') {
    return 'Joint Operating Agreement';
  }
  if (raw === 'MarketingOfftake') {
    return 'Crude offtake';
  }
  return raw || '—';
}

export function formatObligationStatus(status: string): string {
  const raw = status.trim();
  if (raw === 'RentalPastDue') {
    return 'Rental past due';
  }
  if (raw === 'HBP' || raw === 'Hbp') {
    return 'HBP';
  }
  if (raw === 'Expired') {
    return 'Expired';
  }
  if (raw === 'Current') {
    return 'Current';
  }
  return raw || '—';
}

export function formatContractStatus(status: string): string {
  const raw = status.trim();
  if (!raw) {
    return '—';
  }
  const lower = normalizeContractStatus(raw);
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function contractStatusClass(status: string): string {
  const suffix = normalizeContractStatus(status) || 'unknown';
  return `status-pill status-${suffix}`;
}

export function countContractStatuses(rows: readonly { status: string }[]): ContractTileCounts {
  const counts: ContractTileCounts = {
    all: rows.length,
    draft: 0,
    active: 0,
    expired: 0,
    terminated: 0
  };
  for (const row of rows) {
    const key = normalizeContractStatus(row.status);
    if (key === 'draft' || key === 'active' || key === 'expired' || key === 'terminated') {
      counts[key] += 1;
    }
  }
  return counts;
}

export function filterContractsByStatus<T extends { status: string }>(
  rows: readonly T[],
  filter: ContractStatusFilter
): T[] {
  if (filter === 'all') {
    return [...rows];
  }
  return rows.filter((row) => normalizeContractStatus(row.status) === filter);
}

export function paginateRows<T>(rows: readonly T[], page: number, pageSize: number): T[] {
  const start = Math.max(0, (page - 1) * pageSize);
  return rows.slice(start, start + pageSize);
}
